import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, normalizeClass as _normalizeClass, toDisplayString as _toDisplayString, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, unref as _unref, createVNode as _createVNode, renderList as _renderList, withModifiers as _withModifiers } from "vue"

const _hoisted_1 = { key: 0 }
const _hoisted_2 = { class: "profile-header-img-wrapper" }
const _hoisted_3 = { class: "profile-img-container" }
const _hoisted_4 = ["src", "alt"]
const _hoisted_5 = { class: "profile-body" }
const _hoisted_6 = { style: {"font-size":"36px"} }
const _hoisted_7 = { class: "profession" }
const _hoisted_8 = { class: "company" }
const _hoisted_9 = { class: "contact-info-section" }
const _hoisted_10 = ["title"]
const _hoisted_11 = { class: "contact-row" }
const _hoisted_12 = ["title"]
const _hoisted_13 = ["title"]
const _hoisted_14 = {
  key: 1,
  class: "social-icons"
}
const _hoisted_15 = ["href"]
const _hoisted_16 = { class: "profile-footer" }
const _hoisted_17 = { class: "button-group" }
const _hoisted_18 = ["disabled"]
const _hoisted_19 = { class: "pl-1" }
const _hoisted_20 = { class: "qr-modal-content" }
const _hoisted_21 = ["src"]

import { fetchPhotoAsBase64, toVCard, toVCardAndroid, toVCardAndroidMinimal } from "@/composables/getvCard";
import { Icon } from "@iconify/vue";
import defaultImage from "@/assets/images/abc.webp";
import { ref, computed, watchEffect, onUnmounted, onMounted } from "vue";
import {
  getStorage,
  ref as storageRef,
  getDownloadURL,
} from "firebase/storage";
// @ts-ignore - Vue SFC default export
import ContactBottomSheet from "@/components/ContactBottomSheet.vue";
// @ts-ignore - Vue SFC default export
import GetAppBottomSheet from "@/components/GetAppBottomSheet.vue";
import { trackEvent } from "@/utils/analytics";
import type { QwikUCard } from "@/types";

type Props = {
  mysmartcard: QwikUCard;
  isVip: boolean;
};

type SocialMediaConfig = {
  [key: string]: { icon: string };
};

type SocialLink = {
  icon: string;
  url: string;
  label: string;
};

// Move constants outside component to prevent recreation

export default /*@__PURE__*/_defineComponent({
  __name: 'ProfileCard',
  props: {
    mysmartcard: {},
    isVip: { type: Boolean }
  },
  setup(__props: any) {

const storage = getStorage();
const SOCIAL_MEDIA_CONFIG: SocialMediaConfig = {
  X: { icon: 'ri:twitter-x-fill' },
  Twitter: { icon: 'ri:twitter-fill' },
  Facebook: { icon: 'ri:facebook-fill' },
  LinkedIn: { icon: 'ri:linkedin-fill' },
  Instagram: { icon: 'ri:instagram-fill' },
  TikTok: { icon: 'ri:tiktok-fill' },
  YouTube: { icon: 'ri:youtube-fill' },
  Telegram: { icon: 'ri:telegram-fill' },
  WhatsApp: { icon: 'ri:whatsapp-fill' },
  Snapchat: { icon: 'ri:snapchat-fill' },
  GitHub: { icon: 'ri:github-fill' },
  Pinterest: { icon: 'ri:pinterest-fill' },
  Reddit: { icon: 'ri:reddit-fill' },
  Discord: { icon: 'ri:discord-fill' },
  Twitch: { icon: 'ri:twitch-fill' },
  Custom: { icon: 'ri:link' }
};

// Global photo URL cache
const globalPhotoUrlCache = new Map<string, string>();

const props = __props;

const myphotoByte = ref<string | null>(null);
const isDownloading = ref(false);
const imageLoadError = ref(false);
const myphotoUrl = ref<string>(defaultImage);
const showQR = ref(false);
const currentUrl = ref('');
const showContactSheet = ref(false);
const showGetAppSheet = ref(false);

// Memoized photo fetching
const fetchPhotoUrl = async (photoUrl: string): Promise<string> => {
  if (!photoUrl) return defaultImage;

  if (globalPhotoUrlCache.has(photoUrl)) {
    return globalPhotoUrlCache.get(photoUrl) || defaultImage;
  }

  try {
    const imageRef = storageRef(storage, `userImages/${photoUrl}`);
    const url = await getDownloadURL(imageRef);
    globalPhotoUrlCache.set(photoUrl, url);
    return url;
  } catch (error) {
    console.error("Error fetching image URL:", error);
    return defaultImage;
  }
};

const openEmailClient = (emailAddress: string): void => {
  const mailtoLink = `mailto:${emailAddress}`
  window.open(mailtoLink, '_blank')
}

const callPhone = (phoneNumber: string): void => {
  const telLink = `tel:${phoneNumber}`
  window.open(telLink, '_self')
}

const openMap = (address: string): void => {
  const mapLink = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`
  window.open(mapLink, '_blank')
}

// Optimized photo loading with cleanup
const loadPhoto = async (): Promise<void> => {
  if (!props.mysmartcard?.photoUrl) return;

  try {
    const url = await fetchPhotoUrl(props.mysmartcard.photoUrl);
    if (url !== defaultImage) {
      const photoData = await fetchPhotoAsBase64(url);
      myphotoUrl.value = photoData || defaultImage;
    }
  } catch (error) {
    console.error("Error loading photo:", error);
    myphotoUrl.value = defaultImage;
  }
};

// Watch for photo URL changes
watchEffect(() => {
  if (props.mysmartcard?.photoUrl) {
    loadPhoto();
  }
});

// Optimized computed properties
const displayPhotoUrl = computed((): string => {
  try {
    if (!myphotoUrl.value || imageLoadError.value) return defaultImage;

    if (myphotoUrl.value.startsWith('data:image/')) return myphotoUrl.value;
    if (myphotoUrl.value.match(/^[A-Za-z0-9+/=]+$/)) return `data:image/jpeg;base64,${myphotoUrl.value}`;
    if (myphotoUrl.value.startsWith('http')) return myphotoUrl.value;

    return defaultImage;
  } catch {
    return defaultImage;
  }
});

const fullName = computed((): string => {
  const { first = '', last = '' } = props.mysmartcard?.name || {};
  return `${first} ${last}`.trim();
});

const altText = computed((): string =>
  `${fullName.value}'s Profile Picture` || 'Profile Picture'
);

const hasOrganization = computed((): boolean =>
  (props.mysmartcard?.organizations?.length ?? 0) > 0
);

const firstEmail = computed((): string | null => {
  return props.mysmartcard?.emails?.[0]?.address || null;
});

const firstPhone = computed((): string | null => {
  return props.mysmartcard?.phones?.[0]?.number || null;
});

const firstAddress = computed((): string | null => {
  const addr = props.mysmartcard?.addresses?.[0];
  if (!addr) return null;

  // Construct address string
  if (addr.address) return addr.address;

  const parts = [
    addr.street,
    addr.city,
    addr.state,
    addr.postalCode,
    addr.country
  ].filter(Boolean);

  return parts.length > 0 ? parts.join(', ') : null;
});

const firstAddressCountry = computed((): string | null => {
  return props.mysmartcard?.addresses?.[0]?.country || null;
});

const hasSocialMedia = computed((): boolean =>
  (props.mysmartcard?.SmartContent?.some(c => c.contentType === 'socialContent') ?? false)
);

const socialLinks = computed((): SocialLink[] => {
  if (!props.mysmartcard?.SmartContent?.length) return [];

  return props.mysmartcard.SmartContent
    .filter(content => content.contentType === 'socialContent')
    .map((content) => {
      const url = content.contentUrl;
      // Infer platform from URL
      let platform = 'Custom';
      const lowerUrl = url.toLowerCase();

      for (const key of Object.keys(SOCIAL_MEDIA_CONFIG)) {
        if (key === 'Custom') continue;
        if (lowerUrl.includes(key.toLowerCase())) {
          platform = key;
          break;
        }
      }

      // Special cases for common shorteners or variations if needed
      if (platform === 'Custom') {
        if (lowerUrl.includes('t.me')) platform = 'Telegram';
        else if (lowerUrl.includes('fb.com')) platform = 'Facebook';
        else if (lowerUrl.includes('youtu.be')) platform = 'YouTube';
        else if (lowerUrl.includes('x.com')) platform = 'X';
        else if (lowerUrl.includes('wa.me')) platform = 'WhatsApp';
      }

      return {
        icon: SOCIAL_MEDIA_CONFIG[platform].icon,
        url: url,
        label: platform
      };
    });
});

const downloadButtonText = computed((): string =>
  isDownloading.value ? 'Downloading...' : 'Download My Contact'
);

const handleImageError = (e: Event): void => {
  imageLoadError.value = true;
  myphotoUrl.value = defaultImage;
  (e.target as HTMLImageElement).src = defaultImage;
};

// Detect if the device is Android
const isAndroid = (): boolean => {
  return /Android/i.test(navigator.userAgent);
};

const handleDownload = async (): Promise<void> => {
  isDownloading.value = true;

  try {
    // Track download event
    if (props.mysmartcard?.id && props.isVip) {
      trackEvent(props.mysmartcard.id, 'download', {}, props.mysmartcard.companyId);
    }

    // Validate required data
    if (!props.mysmartcard || !fullName.value) {
      throw new Error('Missing required contact information');
    }

    // Ensure we have either the photo data or default image
    const photoData = myphotoUrl.value === defaultImage ? null : myphotoUrl.value;

    // Choose vCard format based on device type for better compatibility
    let vCardData;
    if (isAndroid()) {
      console.log('Android device detected, using Android-optimized vCard format');
      try {
        // First try Android-optimized vCard format with photo
        vCardData = await toVCardAndroid(
          props.mysmartcard,
          photoData,
          {
            withPhoto: Boolean(photoData)
          }
        );
        console.log('Android vCard generated successfully with photo');
      } catch (error) {
        console.warn('Android vCard with photo failed, trying minimal version:', error);
        // Fallback to minimal vCard without photo for problematic Android devices
        vCardData = await toVCardAndroidMinimal(
          props.mysmartcard
        );
        console.log('Android minimal vCard generated successfully');
      }
    } else {
      console.log('Non-Android device detected, using standard vCard format');
      // Use standard vCard format for iOS and other devices
      vCardData = await toVCard(
        props.mysmartcard,
        photoData,
        {
          withPhoto: Boolean(photoData),
          encoding: 'base64'
        }
      );
      console.log('Standard vCard generated successfully');
    }

    console.log('Generated vCard size:', vCardData.length, 'characters');
    console.log('vCard preview:', vCardData.substring(0, 200) + '...');

    // Create and validate blob
    const blob = new Blob([vCardData], { type: "text/vcard;charset=utf-8" });
    if (blob.size === 0) {
      throw new Error('Generated vCard is empty');
    }

    // Create download link
    const url = URL.createObjectURL(blob);
    const filename = `${fullName.value.replace(/[^a-z0-9]/gi, '_')}_contact.vcf`;

    // Create and configure link element
    const link = document.createElement('a');
    link.style.display = 'none';
    link.href = url;
    link.download = filename;

    // Append, click, and cleanup
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    // Cleanup URL object
    setTimeout(() => URL.revokeObjectURL(url), 100);

    // Show the contact sheet after successful download
    showContactSheet.value = true;

  } catch (error: any) {
    console.error('Download failed:', error.message);
    throw new Error(`Failed to download contact: ${error.message}`);
  } finally {
    isDownloading.value = false;
  }
};

const handleContactSheetClose = () => {
  showContactSheet.value = false;
  // Show Get App sheet after a short delay for better UX
  setTimeout(() => {
    showGetAppSheet.value = true;
  }, 300);
};


const handleContactSubmitSuccess = (contactData: any): void => {
  console.log('Contact submitted successfully:', contactData);
  // You could show a success message here
};

const handleContactSubmitError = (error: string): void => {
  console.error('Error submitting contact:', error);
  // You could show an error message here
};

// Get current URL on mount
onMounted(() => {
  currentUrl.value = window.location.href;
});

const toggleQR = (): void => {
  showQR.value = !showQR.value;
};

// Cleanup
onUnmounted(() => {
  myphotoByte.value = null;
  myphotoUrl.value = defaultImage;
});

return (_ctx: any,_cache: any) => {
  return (__props.mysmartcard)
    ? (_openBlock(), _createElementBlock("div", _hoisted_1, [
        _cache[7] || (_cache[7] = _createElementVNode("div", { class: "profile-banner" }, null, -1)),
        _createElementVNode("div", _hoisted_2, [
          _createElementVNode("div", _hoisted_3, [
            _createElementVNode("img", {
              src: displayPhotoUrl.value,
              alt: altText.value,
              class: _normalizeClass(["profile-img", { 'error': imageLoadError.value }]),
              loading: "lazy",
              onError: handleImageError
            }, null, 42, _hoisted_4)
          ])
        ]),
        _createElementVNode("div", _hoisted_5, [
          _createElementVNode("h1", _hoisted_6, _toDisplayString(fullName.value), 1),
          (hasOrganization.value && __props.mysmartcard.organizations?.[0])
            ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                _createElementVNode("p", _hoisted_7, _toDisplayString(__props.mysmartcard.organizations[0]?.title || ''), 1),
                _createElementVNode("p", _hoisted_8, _toDisplayString(__props.mysmartcard.organizations[0]?.company || ''), 1)
              ], 64))
            : _createCommentVNode("", true),
          _createElementVNode("div", _hoisted_9, [
            (firstEmail.value)
              ? (_openBlock(), _createElementBlock("div", {
                  key: 0,
                  class: "contact-item clickable",
                  onClick: _cache[0] || (_cache[0] = ($event: any) => (openEmailClient(firstEmail.value))),
                  title: `Send email to ${firstEmail.value}`
                }, [
                  _createVNode(_unref(Icon), {
                    icon: "mdi:email-outline",
                    width: "20",
                    height: "20",
                    class: "contact-icon"
                  }),
                  _createElementVNode("span", null, _toDisplayString(firstEmail.value), 1)
                ], 8, _hoisted_10))
              : _createCommentVNode("", true),
            _createElementVNode("div", _hoisted_11, [
              (firstPhone.value)
                ? (_openBlock(), _createElementBlock("div", {
                    key: 0,
                    class: "contact-item clickable",
                    onClick: _cache[1] || (_cache[1] = ($event: any) => (callPhone(firstPhone.value))),
                    title: `Call ${firstPhone.value}`
                  }, [
                    _createVNode(_unref(Icon), {
                      icon: "mdi:phone-outline",
                      width: "20",
                      height: "20",
                      class: "contact-icon"
                    }),
                    _createElementVNode("span", null, _toDisplayString(firstPhone.value), 1)
                  ], 8, _hoisted_12))
                : _createCommentVNode("", true),
              (firstAddress.value)
                ? (_openBlock(), _createElementBlock("div", {
                    key: 1,
                    class: "contact-item clickable",
                    onClick: _cache[2] || (_cache[2] = ($event: any) => (openMap(firstAddress.value))),
                    title: `View on map: ${firstAddress.value}`
                  }, [
                    _createVNode(_unref(Icon), {
                      icon: "mdi:map-marker-outline",
                      width: "20",
                      height: "20",
                      class: "contact-icon"
                    }),
                    _createElementVNode("span", null, _toDisplayString(firstAddressCountry.value), 1)
                  ], 8, _hoisted_13))
                : _createCommentVNode("", true)
            ])
          ]),
          (hasSocialMedia.value)
            ? (_openBlock(), _createElementBlock("div", _hoisted_14, [
                (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(socialLinks.value, (link, index) => {
                  return (_openBlock(), _createElementBlock("a", {
                    key: index,
                    href: link.url,
                    target: "_blank"
                  }, [
                    _createVNode(_unref(Icon), {
                      icon: link.icon,
                      width: "16",
                      height: "16"
                    }, null, 8, ["icon"])
                  ], 8, _hoisted_15))
                }), 128))
              ]))
            : _createCommentVNode("", true),
          _createElementVNode("div", _hoisted_16, [
            _createElementVNode("div", _hoisted_17, [
              _createElementVNode("button", {
                onClick: handleDownload,
                class: "btn",
                disabled: isDownloading.value
              }, [
                _createVNode(_unref(Icon), {
                  icon: isDownloading.value ? 'line-md:loading-twotone-loop' : 'line-md:downloading-loop',
                  width: "24",
                  height: "24",
                  style: {"color":"black"}
                }, null, 8, ["icon"]),
                _createElementVNode("span", _hoisted_19, _toDisplayString(downloadButtonText.value), 1)
              ], 8, _hoisted_18),
              _createElementVNode("button", {
                onClick: toggleQR,
                class: "btn qr-btn"
              }, [
                _createVNode(_unref(Icon), {
                  icon: "mdi:qrcode",
                  width: "24",
                  height: "24"
                }),
                _cache[4] || (_cache[4] = _createElementVNode("span", { class: "pl-1" }, "Scan QR", -1))
              ])
            ]),
            (showQR.value)
              ? (_openBlock(), _createElementBlock("div", {
                  key: 0,
                  class: "qr-modal",
                  onClick: _withModifiers(toggleQR, ["self"])
                }, [
                  _createElementVNode("div", _hoisted_20, [
                    _createElementVNode("button", {
                      class: "close-btn",
                      onClick: toggleQR,
                      "aria-label": "Close"
                    }, [
                      _createVNode(_unref(Icon), {
                        icon: "mdi:close",
                        width: "24",
                        height: "24"
                      })
                    ]),
                    _cache[5] || (_cache[5] = _createElementVNode("h3", null, "Scan with your phone", -1)),
                    _createElementVNode("img", {
                      src: 'https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=' + encodeURIComponent(currentUrl.value),
                      alt: "QR Code",
                      class: "qr-code"
                    }, null, 8, _hoisted_21),
                    _cache[6] || (_cache[6] = _createElementVNode("p", { class: "qr-instructions" }, "Scan this QR code with your phone's camera to open this page", -1))
                  ])
                ]))
              : _createCommentVNode("", true)
          ])
        ]),
        _createVNode(ContactBottomSheet, {
          show: showContactSheet.value,
          "recipient-name": fullName.value,
          "card-share-id": __props.mysmartcard._AppShare.cardIdShare,
          "fcm-token": __props.mysmartcard._AppShare?.fcmToken,
          "document-id": __props.mysmartcard.id || '',
          "company-id": __props.mysmartcard.companyId,
          onClose: handleContactSheetClose,
          onSubmitSuccess: handleContactSubmitSuccess,
          onSubmitError: handleContactSubmitError
        }, null, 8, ["show", "recipient-name", "card-share-id", "fcm-token", "document-id", "company-id"]),
        _createVNode(GetAppBottomSheet, {
          show: showGetAppSheet.value,
          onClose: _cache[3] || (_cache[3] = ($event: any) => (showGetAppSheet.value = false))
        }, null, 8, ["show"])
      ]))
    : _createCommentVNode("", true)
}
}

})